/**
 * Input (adapter) → renders the app's ONE text input: your CommonInput.
 * ──────────────────────────────────────────────────────────────────────────
 * The app imports `Input` from the components barrel in many places. This
 * adapter keeps that call-site API (label / value / onChangeText / placeholder
 * / leadingIcon / keyboardType / multiline …) but renders ONLY your
 * `Common_Input_Component` underneath — so every text field in the app is your
 * component. The bottom margin is reset to 0 so existing compact layouts
 * (toolbars, modal rows) aren't disturbed; callers can re-add spacing via
 * `style`.
 */

import React from 'react';
import { ViewStyle, TextStyle, TextInputProps } from 'react-native';
import { useTheme } from '../context/themeContext';
import Icon from './icon';
import CommonInput from './commonInput';

export type InputSize = 'sm' | 'default' | 'lg';

interface InputProps extends Omit<TextInputProps, 'style'> {
  label?: string;
  hint?: string;
  error?: string;
  size?: InputSize;
  leadingIcon?: string | React.ReactNode;
  trailingIcon?: string | React.ReactNode;
  style?: ViewStyle;
  inputStyle?: TextStyle;
}

export default function Input({
  label,
  error,
  leadingIcon,
  trailingIcon,
  style,
  inputStyle,
  value,
  onChangeText,
  placeholder,
  secureTextEntry,
  editable,
  // `size`/`hint` are accepted for call-site compatibility but not used.
  size: _size,
  hint: _hint,
  ...rest
}: InputProps) {
  const { isDark, theme } = useTheme();
  const { colors } = theme;
  const toNode = (ic?: string | React.ReactNode) =>
    typeof ic === 'string'
      ? <Icon name={ic} size={16} color={colors.ink3} />
      : ic;

  return (
    <CommonInput
      label={label}
      error={error}
      value={(value as string) ?? ''}
      onChangeText={onChangeText}
      placeholder={placeholder}
      secureTextEntry={secureTextEntry}
      editable={editable !== false}
      isDark={isDark}
      leftIcon={toNode(leadingIcon)}
      rightIcon={toNode(trailingIcon)}
      containerStyle={[{ marginBottom: 0 }, style]}
      inputProps={{ ...rest, style: inputStyle }}
    />
  );
}
